import React, { useCallback, useMemo, useRef, useState } from "react";
import TableContainer from "components/common/TableContainer";
import { Link } from "react-router-dom";
import {
  downloadQuotationPdf,
  getQuotations,
} from "features/Quotations/services/QuotationService";
import { withLoadingToast } from "utils/withLoadingToast";
import { Quotation } from "features/Quotations/types/quotation.types";
import QuotationWorkflowModals from "features/Quotations/components/QuotationWorkflowModals";
import { useQuotationWorkflow } from "features/Quotations/hooks/useQuotationWorkflow";
import { ColumnDef } from "@tanstack/react-table";
import StatusBadge from "components/common/StatusBadge";
import {
  QuotationPdfFormat,
  QuotationSourceLabelMap,
  QuotationStatusMap,
  quotationWorkflowFlags,
} from "features/Quotations/constants/quotation.constants";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import {
  CodeTitleColumns,
  IndexColumn,
  SourceColumn,
  resolveMappedSourceCell,
} from "components/common/columns";
import { DateOnly, DateTime } from "components/common/Date";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { formatCurrency } from "utils/currency";
import QuotationListFilters from "./QuotationListFilters";

export const QuotationsTable = () => {
  const { can } = useAccess();
  const canUpdate = can(Permissions.QUOTATION.UPDATE);
  const canSend = can(Permissions.QUOTATION.SEND);
  const canReject = can(Permissions.QUOTATION.REJECT);
  const tableRef = useRef<() => void>(null);

  const [statusFilter, setStatusFilter] = useState("");
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [clientIdFilter, setClientIdFilter] = useState<number | null>(null);

  const requestParams = useMemo(
    () => ({
      status: statusFilter || undefined,
      sourceType: sourceTypeFilter || undefined,
      clientId: clientIdFilter ?? undefined,
    }),
    [clientIdFilter, sourceTypeFilter, statusFilter],
  );

  const filtersBadgeCount = useMemo(() => {
    let n = 0;
    if (statusFilter) n += 1;
    if (sourceTypeFilter) n += 1;
    if (clientIdFilter != null) n += 1;
    return n;
  }, [clientIdFilter, sourceTypeFilter, statusFilter]);

  const clearListFilters = useCallback(() => {
    setStatusFilter("");
    setSourceTypeFilter("");
    setClientIdFilter(null);
  }, []);

  const workflow = useQuotationWorkflow({
    onSuccess: () => tableRef.current?.(),
  });

  const { openAcceptModal, openCancelModal, openRejectModal } = workflow;

  const onDownloadPdf = useCallback(
    async (r: Quotation, format: QuotationPdfFormat) => {
      await withLoadingToast(
        format === QuotationPdfFormat.BOQ
          ? "Preparing BOQ PDF…"
          : "Preparing quotation PDF…",
        () => downloadQuotationPdf(r.id, r.code, format),
        format === QuotationPdfFormat.BOQ
          ? "BOQ PDF downloaded"
          : "Quotation PDF downloaded",
        "Could not download quotation PDF",
        (err) =>
          err instanceof Error
            ? err.message
            : "Could not download quotation PDF",
      );
    },
    [],
  );

  const columns: ColumnDef<Quotation>[] = useMemo(
    () => [
      IndexColumn<Quotation>(),
      ...CodeTitleColumns<Quotation>({
        titleHeader: "Quotation",
        accessorKey: "code",
        resolve: (r) => ({
          code: r.code,
          title: r.title?.trim(),
          to: `/quotations/${r.id}`,
        }),
      }),

      SourceColumn<Quotation>({
        id: "source",
        resolve: (row) =>
          resolveMappedSourceCell(row.sourceType, row.sourceId, {
            typeLabelMap: QuotationSourceLabelMap,
            source: row.source,
          }),
      }),

      {
        header: "Total",
        accessorKey: "total",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const val = row.original.total;
          return val != null ? (
            <span className="fw-medium">{formatCurrency(val)}</span>
          ) : (
            <span className="text-muted">—</span>
          );
        },
      },

      {
        header: "Valid Till",
        accessorKey: "validUntil",
        cell: ({ row }) => {
          return <DateOnly date={row.original.validUntil} />;
        },
        enableColumnFilter: false,
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => {
          const val = row.original.status;
          return <StatusBadge value={val} map={QuotationStatusMap} />;
        },
        enableColumnFilter: false,
      },
      {
        header: "Created",
        accessorKey: "created_at",
        enableColumnFilter: false,
        cell: ({ row }) => {
          return <DateTime date={row.original.created_at} />;
        },
      },
      {
        header: "Actions",
        id: "actions",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const record = row.original;
          const wf = quotationWorkflowFlags(record.status);

          return (
            <UncontrolledDropdown>
              <DropdownToggle
                type="button"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
                aria-label="Quotation actions"
              >
                <i className="ri-more-2-fill align-middle" />
              </DropdownToggle>
              <DropdownMenu container="body" end>
                <DropdownItem tag={Link} to={`/quotations/${record.id}`}>
                  <i className="ri-eye-line align-bottom me-2 text-muted" />
                  View
                </DropdownItem>
                <DropdownItem
                  tag={Link}
                  to={`/quotations/edit/${record.id}`}
                  disabled={!wf.canEdit}
                  title={
                    !wf.canEdit
                      ? "Only draft quotations can be edited"
                      : undefined
                  }
                >
                  <i className="ri-pencil-line align-bottom me-2 text-muted" />
                  Edit
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    void onDownloadPdf(record, QuotationPdfFormat.NORMAL)
                  }
                >
                  <i className="ri-file-pdf-line align-bottom me-2 text-muted" />
                  Export standard PDF
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    void onDownloadPdf(record, QuotationPdfFormat.BOQ)
                  }
                >
                  <i className="ri-file-list-3-line align-bottom me-2 text-muted" />
                  Export BOQ PDF
                </DropdownItem>
                {canUpdate || canSend || canReject ? (
                  <>
                    {/* {canSend && (wf.canSend || wf.canResend) ? (
                      <>
                        <DropdownItem divider />
                        <DropdownItem
                          onClick={() =>
                            wf.canSend
                              ? openSendConfirm(record)
                              : openResendConfirm(record)
                          }
                          title={
                            wf.canSend
                              ? "Email PDF to the client (client must have an email)"
                              : "Re-email PDF to the client without changing status"
                          }
                        >
                          <i
                            className={`${
                              wf.canSend
                                ? "ri-send-plane-line"
                                : "ri-mail-send-line"
                            } align-bottom me-2 text-primary`}
                          />
                          {wf.canSend
                            ? "Send to customer"
                            : "Resend to customer"}
                        </DropdownItem>
                      </>
                    ) : null} */}
                    {canUpdate ? (
                      <>
                        <DropdownItem
                          onClick={() => openAcceptModal(record)}
                          disabled={!wf.canAccept}
                          title={
                            !wf.canAccept
                              ? "Accept is only available after the quotation is sent"
                              : undefined
                          }
                        >
                          <i className="ri-check-double-line align-bottom me-2 text-success" />
                          Accept
                        </DropdownItem>
                        <DropdownItem
                          onClick={() => openCancelModal(record)}
                          disabled={!wf.canCancel}
                          title={
                            !wf.canCancel
                              ? "Only draft or sent quotations can be cancelled"
                              : undefined
                          }
                        >
                          <i className="ri-close-circle-line align-bottom me-2 text-warning" />
                          Cancel quotation
                        </DropdownItem>
                      </>
                    ) : null}

                    {canReject ? (
                      <DropdownItem
                        onClick={() => openRejectModal(record)}
                        disabled={!wf.canReject}
                        title={
                          !wf.canReject
                            ? "Only sent quotations can be rejected"
                            : undefined
                        }
                      >
                        <i className="ri-thumb-down-line align-bottom me-2 text-danger" />
                        Reject quotation
                      </DropdownItem>
                    ) : null}
                  </>
                ) : null}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [
      canSend,
      canUpdate,
      canReject,
      onDownloadPdf,
      // openSendConfirm,
      // openResendConfirm,
      openAcceptModal,
      openCancelModal,
      openRejectModal,
    ],
  );

  return (
    <React.Fragment>
      <QuotationWorkflowModals workflow={workflow} />

      <TableContainer
        columns={columns}
        fetchApi={getQuotations}
        requestParams={requestParams}
        pageSize={10}
        globalSearch={true}
        refreshRef={tableRef}
        divClass="table-responsive table-card mb-3"
        tableClass="align-middle table-nowrap mb-0"
        theadClass="table-light text-muted"
        toolbarRight={
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <QuotationListFilters
              status={statusFilter}
              onStatusChange={setStatusFilter}
              sourceType={sourceTypeFilter}
              onSourceTypeChange={setSourceTypeFilter}
              clientId={clientIdFilter}
              onClientIdChange={setClientIdFilter}
            />
            {filtersBadgeCount > 0 && (
              <button
                type="button"
                className="btn btn-soft-danger d-flex align-items-center gap-1"
                onClick={clearListFilters}
              >
                <i className="ri-close-circle-line" />
                Clear
              </button>
            )}
          </div>
        }
      />
    </React.Fragment>
  );
};
